/**
 * SpectatorThumbnail — "TV screen" tile for the spectator multiplex grid.
 *
 * v1 renders a lightweight text placeholder (phase label + last trick as
 * mini card codes) rather than a full mini card table, per spec §Frontend/2.
 */

import type { SpectatorPhasePreview, WatchableSourceSummary } from '../hooks/useSpectatorPlayback';

const PHASE_LABELS: Record<SpectatorPhasePreview['phase'], string> = {
  attente: 'En attente…',
  enchere: 'Enchères en cours',
  chien: 'Chien visible',
  ecart: 'Écart en cours',
  jeu: 'Cartes sur le tapis',
  score: 'Fin de donne',
};

const MODE_LABELS: Record<WatchableSourceSummary['mode'], string> = {
  live: 'Live',
  neo_libre: 'Neo libre',
  neo_compet: 'Neo compét.',
};

function cardLabel(card: number): string {
  if (card === 78) return '🃏';
  const suits = ['♠', '♥', '♣', '♦'];
  if (card >= 1 && card <= 14) return `${card}${suits[0]}`;
  if (card >= 15 && card <= 28) return `${card - 14}${suits[1]}`;
  if (card >= 29 && card <= 42) return `${card - 28}${suits[2]}`;
  if (card >= 43 && card <= 56) return `${card - 42}${suits[3]}`;
  if (card >= 57) return `T${card - 56}`;
  return String(card);
}

const TOP3_BADGE: Record<1 | 2 | 3, string> = {
  1: '🥇',
  2: '🥈',
  3: '🥉',
};

interface SpectatorThumbnailProps {
  source: WatchableSourceSummary;
  selected?: boolean;
  /** Rang (1/2/3) au classement du tournoi Live en cours, pour mise en évidence. */
  highlightRank?: 1 | 2 | 3;
  onClick: () => void;
}

export default function SpectatorThumbnail({ source, selected, highlightRank, onClick }: SpectatorThumbnailProps) {
  const phase = source.phasePreview?.phase ?? 'attente';
  const label = source.phasePreview ? PHASE_LABELS[phase] : 'En attente…';
  const lastTrick = source.phasePreview?.lastTrick;

  return (
    <div
      className={`spectator-thumbnail ${selected ? 'selected' : ''} ${highlightRank ? `top-rank top-rank-${highlightRank}` : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      {highlightRank && (
        <span
          className="spectator-thumbnail-top-badge"
          title={`${highlightRank}${highlightRank === 1 ? 'er' : 'ème'} au classement du tournoi en cours`}
        >
          {TOP3_BADGE[highlightRank]}
        </span>
      )}
      <div className="spectator-thumbnail-header">
        <span className="spectator-thumbnail-title">{source.tableLabel}</span>
        <span className={`spectator-thumbnail-mode mode-${source.mode}`}>{MODE_LABELS[source.mode]}</span>
      </div>

      <div className="spectator-thumbnail-screen">
        <span className="spectator-thumbnail-phase">{label}</span>
        {lastTrick && lastTrick.length > 0 && (
          <div className="spectator-thumbnail-trick">
            {lastTrick.map((c, i) => (
              <span key={i} className="spectator-thumbnail-card">{cardLabel(c.card)}</span>
            ))}
          </div>
        )}
      </div>

      <div className="spectator-thumbnail-meta">
        {source.players.map(p => p.pseudo).join(', ') || source.playerPseudo || '—'}
      </div>
      <div className="spectator-thumbnail-meta">
        {source.liveDonnes
          ? `Donne ${source.liveDonnes.current}/${source.liveDonnes.max}`
          : `Donne en cours : ${source.currentDealNumber}`}
        {source.playableDealNumber != null && ` · Diffusible : ${source.playableDealNumber}`}
      </div>
      {source.liveRanking?.clTournoi !== undefined && (
        <div className="spectator-thumbnail-meta spectator-thumbnail-ranking">
          Classement tournoi : {source.liveRanking.clTournoi}
          {source.liveRanking.pointsTournoi !== undefined && ` (${source.liveRanking.pointsTournoi} pts)`}
        </div>
      )}
    </div>
  );
}
